import React from 'react';
import axiosInstance from '../../../../Components/Request';
import Card from '../../../components/ui/Card/Card';
import Button from '../../../components/ui/Button/Button';
import Input from '../../../components/ui/Input/Input';
import Tooltip from '../../../components/ui/Tooltip/Tooltip';
import { MailIcon, AlertTriangleIcon } from '../../../components/ui/Icons';
import styles from './NotificationContactsCard.module.css';

/**
 * CONTACTOS DEL CLIENTE PARA AVISOS POR CORREO.
 *
 * ## 🔑 Capturar y INVITAR son dos botones distintos, a propósito
 *
 * Lo cómodo sería que guardar el correo mandara la invitación de una vez. No se hace, y no es por prudencia
 * genérica: **mandarle un correo a alguien de fuera no se deshace**. Si fuera el efecto secundario de guardar un
 * formulario, un dedazo al capturar acabaría en la bandeja de un tercero, y la única respuesta posible sería
 * pedir perdón.
 *
 * 📌 Así que se captura, se revisa la dirección escrita, y después alguien decide mandar.
 *
 * ## Los cuatro estados y qué acción pide cada uno
 *
 * | Estado | Qué significa | Qué se ofrece |
 * |---|---|---|
 * | `pending` | capturado, no recibe nada | invitar |
 * | `active` | aceptó, recibe | preferencias, y dar de baja |
 * | `unsubscribed` | se dio de baja **él** | reactivar (vuelve a `pending`) |
 * | `bounced` | el buzón rebota | corregir la dirección |
 *
 * 🔴 **`unsubscribed` no se puede volver a `active` desde aquí.** Reactivar lo devuelve a `pending` y tiene que
 * aceptar otra vez. Si desde dentro se pudiera ponerlo a recibir, el enlace de baja de los correos no valdría
 * nada — y ese enlace es lo que evita que la gente marque spam y arrastre la reputación del dominio.
 */

interface Contact {
  id: string;
  email: string;
  label: string | null;
  status: 'pending' | 'active' | 'unsubscribed' | 'bounced';
  verifiedAt: string | null;
  invitedAt: string | null;
  unsubscribedAt: string | null;
  unsubscribeSource: string | null;
  invitePending: boolean;
  inviteExpired: boolean;
  onStatusChange: boolean;
  onDelayed: boolean;
  onScheduledDigest: boolean;
  sendHours: string | null;
  timezone: string;
  onlyOnChange: boolean;
}

interface Props {
  clientId: string;
  canEdit: boolean;
}

const STATUS_LABEL: Record<Contact['status'], string> = {
  pending: 'NOT CONFIRMED',
  active: 'RECEIVING',
  unsubscribed: 'UNSUBSCRIBED',
  bounced: 'BOUNCING',
};

const NotificationContactsCard: React.FC<Props> = ({ clientId, canEdit }) => {
  const [items, setItems] = React.useState<Contact[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [feedback, setFeedback] = React.useState<{ ok: boolean; message: string } | null>(null);

  const [email, setEmail] = React.useState('');
  const [label, setLabel] = React.useState('');
  const [expanded, setExpanded] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`/clients/${clientId}/contacts`);
      setItems(data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const act = async (key: string, run: () => Promise<{ data: any }>) => {
    setBusy(key);
    setFeedback(null);
    try {
      const { data } = await run();
      setFeedback({ ok: true, message: data?.message || 'Done.' });
      await load();
    } catch (err: any) {
      setFeedback({
        ok: false,
        message: err?.response?.data?.message || 'That did not go through.',
      });
    } finally {
      setBusy(null);
    }
  };

  const add = async () => {
    const value = email.trim();
    if (!value) return;
    await act('add', () =>
      axiosInstance.post(`/clients/${clientId}/contacts`, {
        email: value,
        label: label.trim() || null,
      })
    );
    setEmail('');
    setLabel('');
  };

  const savePreference = (contact: Contact, changes: Record<string, unknown>) =>
    act(contact.id, () => axiosInstance.put(`/client-contacts/${contact.id}`, changes));

  return (
    <Card className={styles.card}>
      <div className={styles.head}>
        <h3 className={styles.title}>Automatic shipment updates</h3>
        <p className={styles.sub}>
          Addresses at this client that get emailed when their shipments move. They confirm from their
          own inbox — nobody here can confirm for them, or the check would mean nothing.
        </p>
      </div>

      {feedback && (
        <div className={[styles.feedback, feedback.ok ? styles.fbOk : styles.fbBad].join(' ')}>
          {!feedback.ok && <AlertTriangleIcon size={14} />}
          <span>{feedback.message}</span>
        </div>
      )}

      {canEdit && (
        <div className={styles.addRow}>
          <Input
            value={email}
            placeholder="name@client.com"
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
          />
          {/**
           * 📌 La etiqueta no es decoración: "Almacén MTY" dice a quién se le está escribiendo, y una lista de
           * `op4@…` no dice nada cuando hay que decidir a quién quitar.
           */}
          <Input
            value={label}
            placeholder="Who is it? e.g. Warehouse MTY"
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
          />
          <Button
            variant="primary"
            className={styles.addBtn}
            disabled={!email.trim() || busy === 'add'}
            onClick={add}
          >
            {busy === 'add' ? 'Saving…' : 'Add'}
          </Button>
        </div>
      )}
      {canEdit && (
        <span className={styles.addHint}>
          Saving does not email anyone. Sending the invitation is a separate button — a typo here would
          otherwise land in a stranger's inbox, and that cannot be undone.
        </span>
      )}

      {loading && <p className={styles.empty}>Loading…</p>}
      {!loading && items.length === 0 && (
        <p className={styles.empty}>
          No addresses yet. Nothing is emailed to this client automatically.
        </p>
      )}

      <div className={styles.list}>
        {items.map((c) => (
          <div key={c.id} className={styles.row}>
            <div className={styles.rowMain}>
              <div className={styles.rowHead}>
                <span className={styles.email}>{c.email}</span>
                {c.label && <span className={styles.label}>{c.label}</span>}
                <span className={[styles.badge, styles[`badge_${c.status}`]].join(' ')}>
                  {STATUS_LABEL[c.status]}
                </span>
                {/**
                 * 🔑 Se distingue una invitación **viva** de una **caducada**. Son dos situaciones que se ven
                 * iguales —"no confirmado"— y piden lo contrario: en la primera hay que esperar, en la segunda
                 * hay que volver a mandar. Sin la distinción, se reinvita a quien ya tiene el correo abierto.
                 */}
                {c.status === 'pending' && c.invitePending && (
                  <span className={styles.hintTag}>invitation sent, waiting</span>
                )}
                {c.status === 'pending' && c.inviteExpired && (
                  <span className={styles.warnTag}>invitation expired</span>
                )}
              </div>

              {c.status === 'unsubscribed' && (
                <p className={styles.reason}>
                  {/**
                   * 📌 Se dice **quién** lo dio de baja. `internal` significa que alguien de aquí lo hizo a
                   * pedido del cliente; `link` o `header`, que el cliente lo hizo solo. Son dos conversaciones
                   * distintas cuando pregunta por qué dejó de recibir.
                   */}
                  Stopped{' '}
                  {c.unsubscribeSource === 'internal'
                    ? 'by Dylo, at their request'
                    : c.unsubscribeSource === 'header'
                    ? 'from the unsubscribe button in their email app'
                    : 'from the link in one of our emails'}
                  . Re-inviting is blocked until it is reactivated.
                </p>
              )}

              {c.status === 'active' && (
                <button
                  type="button"
                  className={styles.expander}
                  onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                >
                  {expanded === c.id ? 'Hide what they get' : 'What they get'}
                </button>
              )}

              {expanded === c.id && c.status === 'active' && (
                <div className={styles.prefs}>
                  <label className={styles.check}>
                    <input
                      type="checkbox"
                      checked={c.onStatusChange}
                      disabled={!canEdit || busy === c.id}
                      onChange={(e) =>
                        savePreference(c, { on_status_change: e.target.checked })
                      }
                    />
                    <span>Status changes</span>
                  </label>
                  <label className={styles.check}>
                    <input
                      type="checkbox"
                      checked={c.onDelayed}
                      disabled={!canEdit || busy === c.id}
                      onChange={(e) => savePreference(c, { on_delayed: e.target.checked })}
                    />
                    <span>Delays</span>
                  </label>
                  <label className={styles.check}>
                    <input
                      type="checkbox"
                      checked={c.onScheduledDigest}
                      disabled={!canEdit || busy === c.id}
                      onChange={(e) =>
                        savePreference(c, { on_scheduled_digest: e.target.checked })
                      }
                    />
                    <span>Scheduled summary</span>
                  </label>
                  {/**
                   * 📌 El horario solo aparece con el resumen encendido. Mostrarlo siempre invitaría a
                   * configurar horas para algo que no se manda a ninguna hora — los avisos de cambio salen
                   * cuando el embarque se mueve, no a las 8.
                   */}
                  {c.onScheduledDigest && (
                    <span className={styles.prefMeta}>
                      at {c.sendHours || '08:00'} · {c.timezone}
                      {c.onlyOnChange ? ' · only when something moved' : ' · even with no movement'}
                    </span>
                  )}
                </div>
              )}
            </div>

            {canEdit && (
              <div className={styles.rowActions}>
                {(c.status === 'pending' || c.status === 'bounced') && (
                  <Tooltip
                    content={
                      c.invitePending
                        ? 'Send it again — the previous link stops working'
                        : 'Send the invitation to confirm this address'
                    }
                  >
                    <button
                      className={styles.actionBtn}
                      disabled={busy === c.id}
                      onClick={() =>
                        act(c.id, () => axiosInstance.post(`/client-contacts/${c.id}/invite`))
                      }
                    >
                      <MailIcon size={13} />
                      {c.invitePending || c.inviteExpired ? 'Send again' : 'Send invitation'}
                    </button>
                  </Tooltip>
                )}

                {c.status === 'active' && (
                  <Tooltip content="They asked us to stop by phone or email">
                    <button
                      className={styles.actionBtn}
                      disabled={busy === c.id}
                      onClick={() =>
                        act(c.id, () =>
                          axiosInstance.post(`/client-contacts/${c.id}/unsubscribe`)
                        )
                      }
                    >
                      Stop updates
                    </button>
                  </Tooltip>
                )}

                {c.status === 'unsubscribed' && (
                  <Tooltip content="Goes back to pending — they have to accept again">
                    <button
                      className={styles.actionBtn}
                      disabled={busy === c.id}
                      onClick={() =>
                        act(c.id, () => axiosInstance.post(`/client-contacts/${c.id}/reactivate`))
                      }
                    >
                      Reactivate
                    </button>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
};

export default NotificationContactsCard;
